import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';

// Components
import Layout from './components/layout/Layout';
import SecurityMiddleware from './components/security/SecurityMiddleware';

// Pages
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import Inicio from './pages/Inicio';
import Agendamentos from './pages/Agendamentos';
import Cargas from './pages/Cargas';
import Solicitacoes from './pages/Solicitacoes';
import GestaoPedidosCompra from './pages/GestaoPedidosCompra';
import GestaoAgendamento from './pages/GestaoAgendamento';
import BIAnalitico from './pages/BIAnalitico';
import AreaRCA from './pages/AreaRCA';
import MonitoramentoSistema from './pages/MonitoramentoSistema';
import Usuarios from './pages/Usuarios';
import VisualizarSolicitacao from './pages/VisualizarSolicitacao';
import Configuracoes from './pages/Configuracoes';
import Filiais from './pages/Filiais';
import PoliticaPrivacidade from './pages/PoliticaPrivacidade';
import MeusDados from './pages/MeusDados';
import AgendarVisitas from './pages/AgendarVisitas';
import GestaoVisitas from './pages/GestaoVisitas';
import AbaProtegida from './components/common/AbaProtegida';

// Context
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { AppProvider } from './contexts/AppContext';

// Protected Route Component
const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }
  
  if (!user) {
    return <Navigate to="/login" replace />;
  }
  
  return <>{children}</>;
};

function App() {
  return (
    <Router>
      <SecurityMiddleware>
        <AuthProvider>
          <AppProvider>
            <Routes>
              <Route path="/login" element={<Login />} />
              <Route path="/politica-privacidade" element={<PoliticaPrivacidade />} />
              <Route path="/" element={<ProtectedRoute><Layout /></ProtectedRoute>}>
                <Route path="inicio" element={
                  <AbaProtegida nomeAba="Início">
                    <Inicio />
                  </AbaProtegida>
                } />
                <Route path="dashboard" element={
                  <AbaProtegida nomeAba="Dashboard">
                    <Dashboard />
                  </AbaProtegida>
                } />
                <Route path="agendamentos" element={
                  <AbaProtegida nomeAba="Agendamentos">
                    <Agendamentos />
                  </AbaProtegida>
                } />
                <Route path="cargas" element={
                  <AbaProtegida nomeAba="Cargas">
                    <Cargas />
                  </AbaProtegida>
                } />
                <Route path="solicitacoes" element={
                  <AbaProtegida nomeAba="Solicitações">
                    <Solicitacoes />
                  </AbaProtegida>
                } />
                <Route path="visualizar-solicitacao/:id" element={
                  <AbaProtegida nomeAba="Visualizar Solicitação">
                    <VisualizarSolicitacao />
                  </AbaProtegida>
                } />
                <Route path="gestao-pedidos" element={
                  <AbaProtegida nomeAba="Gestão de Pedidos">
                    <GestaoPedidosCompra />
                  </AbaProtegida>
                } />
                <Route path="gestao-agendamento" element={
                  <AbaProtegida nomeAba="Gestão de Agendamento">
                    <GestaoAgendamento />
                  </AbaProtegida>
                } />
                <Route path="bi-analitico" element={
                  <AbaProtegida nomeAba="BI Analítico">
                    <BIAnalitico />
                  </AbaProtegida>
                } />
                <Route path="area-rca" element={
                  <AbaProtegida nomeAba="Área do RCA">
                    <AreaRCA />
                  </AbaProtegida>
                } />
                <Route path="monitoramento" element={
                  <AbaProtegida nomeAba="Monitoramento de Sistema">
                    <MonitoramentoSistema />
                  </AbaProtegida>
                } />
                <Route path="filiais" element={<Filiais />} />
                <Route path="usuarios" element={
                  <AbaProtegida nomeAba="Usuários">
                    <Usuarios />
                  </AbaProtegida>
                } />
                <Route path="configuracoes" element={
                  <AbaProtegida nomeAba="Configurações">
                    <Configuracoes />
                  </AbaProtegida>
                } />
                <Route path="agendar-visitas" element={
                  <AbaProtegida nomeAba="Agendar Visitas">
                    <AgendarVisitas />
                  </AbaProtegida>
                } />
                <Route path="gestao-visitas" element={
                  <AbaProtegida nomeAba="Gestão de Visitas">
                    <GestaoVisitas />
                  </AbaProtegida>
                } />
                <Route path="meus-dados" element={<MeusDados />} />
                <Route path="relatorios" element={<div className="p-6"><h1 className="text-2xl font-bold">Relatórios</h1></div>} />
              </Route>
            </Routes>
            <Toaster
              position="top-right"
              toastOptions={{
                duration: 4000,
                style: {
                  background: '#363636',
                  color: '#fff',
                },
                success: {
                  duration: 3000,
                  iconTheme: {
                    primary: '#4ade80',
                    secondary: '#fff',
                  },
                },
                error: {
                  duration: 5000,
                  iconTheme: {
                    primary: '#ef4444',
                    secondary: '#fff',
                  },
                },
              }}
            />
          </AppProvider>
      </AuthProvider>
      </SecurityMiddleware>
    </Router>
  );
}

export default App;
